/* ==========================================================
   assets/hero-slider.js
   Hero Slider — lightweight, dependency-free, OS 2.0
   ========================================================== */

(function () {
  'use strict';

  var PREFERS_REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function HeroSlider(root) {
    this.root = root;
    this.viewport = root.querySelector('[data-hero-slider-viewport]');
    this.track = root.querySelector('[data-hero-slider-track]');
    this.slides = Array.prototype.slice.call(root.querySelectorAll('[data-hero-slide]'));
    this.prevBtn = root.querySelector('[data-hero-slider-prev]');
    this.nextBtn = root.querySelector('[data-hero-slider-next]');
    this.dots = Array.prototype.slice.call(root.querySelectorAll('[data-hero-slider-dot]'));

    this.enabled = root.getAttribute('data-enable-slider') === 'true';
    this.autoplay = root.getAttribute('data-autoplay') === 'true' && !PREFERS_REDUCED_MOTION;
    this.autoplaySpeed = (parseFloat(root.getAttribute('data-autoplay-speed')) || 5) * 1000;
    this.pauseOnHover = root.getAttribute('data-pause-on-hover') === 'true';
    this.infiniteLoop = root.getAttribute('data-infinite-loop') === 'true';
    this.transitionType = root.getAttribute('data-transition-type') || 'slide';

    this.current = 0;
    this.timer = null;

    if (this.slides.length <= 1) {
      this.enabled = false;
    }

    this.init();
  }

  HeroSlider.prototype.init = function () {
    this.initVideos();

    if (!this.enabled) {
      return;
    }

    this.bindControls();
    this.bindKeyboard();
    this.bindTouch();

    if (this.pauseOnHover) {
      this.root.addEventListener('mouseenter', this.stopAutoplay.bind(this));
      this.root.addEventListener('mouseleave', this.startAutoplay.bind(this));
    }

    this.goTo(0, false);
    this.startAutoplay();
  };

  /* ---------------- Video: lazy-load sources, play only when active ---------------- */
  HeroSlider.prototype.initVideos = function () {
    this.slides.forEach(function (slide, index) {
      var videos = slide.querySelectorAll('[data-hero-video]');
      if (!videos.length) return;

      videos.forEach(function (video) {
        var sources = video.querySelectorAll('source[data-src]');
        var hydrate = function () {
          sources.forEach(function (source) {
            if (!source.src) {
              source.src = source.getAttribute('data-src');
            }
          });
          video.load();
          video.addEventListener('loadeddata', function () {
            video.classList.add('is-loaded');
          }, { once: true });
        };

        if (index === 0) {
          hydrate();
          if (video.hasAttribute('autoplay')) {
            var playPromise = video.play();
            if (playPromise && playPromise.catch) playPromise.catch(function () {});
          }
        } else {
          video._hydrate = hydrate;
        }
      });
    });
  };

  HeroSlider.prototype.activateSlideVideo = function (slide) {
    var videos = slide.querySelectorAll('[data-hero-video]');
    videos.forEach(function (video) {
      if (video._hydrate) {
        video._hydrate();
        video._hydrate = null;
      }
      if (video.hasAttribute('autoplay')) {
        var p = video.play();
        if (p && p.catch) p.catch(function () {});
      }
    });
  };

  HeroSlider.prototype.deactivateSlideVideo = function (slide) {
    var videos = slide.querySelectorAll('[data-hero-video]');
    videos.forEach(function (video) {
      if (!video.paused) video.pause();
    });
  };

  /* ---------------- Controls ---------------- */
  HeroSlider.prototype.bindControls = function () {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', this.prev.bind(this));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', this.next.bind(this));
    }
    this.dots.forEach(function (dot, index) {
      dot.addEventListener('click', function () {
        this.goTo(index, true);
        this.restartAutoplay();
      }.bind(this));
    }.bind(this));
  };

  HeroSlider.prototype.bindKeyboard = function () {
    this.root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') {
        this.prev();
        this.restartAutoplay();
      } else if (e.key === 'ArrowRight') {
        this.next();
        this.restartAutoplay();
      }
    }.bind(this));
  };

  HeroSlider.prototype.bindTouch = function () {
    var startX = 0;
    var isDown = false;

    this.viewport.addEventListener('touchstart', function (e) {
      startX = e.touches[0].clientX;
      isDown = true;
    }, { passive: true });

    this.viewport.addEventListener('touchend', function (e) {
      if (!isDown) return;
      isDown = false;
      var diff = e.changedTouches[0].clientX - startX;
      if (Math.abs(diff) < 40) return;
      if (diff < 0) {
        this.next();
      } else {
        this.prev();
      }
      this.restartAutoplay();
    }.bind(this), { passive: true });
  };

  /* ---------------- Navigation ---------------- */
  HeroSlider.prototype.prev = function () {
    var target = this.current - 1;
    if (target < 0) {
      if (!this.infiniteLoop) return;
      target = this.slides.length - 1;
    }
    this.goTo(target, true);
  };

  HeroSlider.prototype.next = function () {
    var target = this.current + 1;
    if (target >= this.slides.length) {
      if (!this.infiniteLoop) return;
      target = 0;
    }
    this.goTo(target, true);
  };

  HeroSlider.prototype.goTo = function (index, userInitiated) {
    var previousSlide = this.slides[this.current];
    var nextSlide = this.slides[index];

    if (this.transitionType === 'slide') {
      this.track.style.transform = 'translateX(-' + (index * 100) + '%)';
    }

    this.slides.forEach(function (slide, i) {
      var active = i === index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    this.dots.forEach(function (dot, i) {
      var active = i === index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    if (previousSlide && previousSlide !== nextSlide) {
      this.deactivateSlideVideo(previousSlide);
    }
    if (nextSlide) {
      this.activateSlideVideo(nextSlide);
    }

    this.current = index;
  };

  /* ---------------- Autoplay ---------------- */
  HeroSlider.prototype.startAutoplay = function () {
    if (!this.autoplay || this.timer) return;
    this.timer = setInterval(this.next.bind(this), this.autoplaySpeed);
  };

  HeroSlider.prototype.stopAutoplay = function () {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  };

  HeroSlider.prototype.restartAutoplay = function () {
    if (!this.autoplay) return;
    this.stopAutoplay();
    this.startAutoplay();
  };

  /* ---------------- Boot ---------------- */
  function initAll() {
    var sliders = document.querySelectorAll('[data-hero-slider]');
    sliders.forEach(function (el) {
      if (!el._heroSliderInstance) {
        el._heroSliderInstance = new HeroSlider(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init when Shopify theme editor injects/updates sections
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector('[data-hero-slider]');
    if (el && !el._heroSliderInstance) {
      el._heroSliderInstance = new HeroSlider(el);
    }
  });
})();
